import React from "react";
import { Box, IconButton, styled, Typography } from "@mui/material";
import { Add, Remove, Delete } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { CartItem } from "../../types/types";
import { calculateDiscountedPrice, formatCOP } from "../../utils/format";
import { isPricedLine } from "../../utils/availability";
import { cartLineKey } from "../../services/cartSlice/cartSlice";
import AvailabilityChip from "../AvailabilityChip/AvailabilityChip";
import { CART_TEXTS } from "../../pages/shared/constants";

const CartItemCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  boxShadow: theme.shadows[1],
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
}));

const CartItemRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const QuantityControls = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  boxShadow: theme.shadows[1],
  borderRadius: theme.spacing(0.5),
}));

const QuantityButton = styled(Box)(({ theme }) => ({
  boxShadow: theme.shadows[1],
  borderRadius: theme.spacing(0.5),
}));

const QuoteNote = styled(Typography)(({ theme }) => ({
  color: theme.palette.purchase.quote,
  fontWeight: 700,
}));

const DeleteButtonWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginRight: theme.spacing(2),
  boxShadow: theme.shadows[1],
  borderRadius: theme.spacing(1),
}));

interface CartItemProps {
  item: CartItem;
  /** Keyed by the cart's own line key, not by name — see `cartLineKey`. */
  updateCartItem: (key: string, newQuantity: number) => void;
  removeCartItem: (key: string) => void;
}

const CartItemComponent: React.FC<CartItemProps> = ({ item, updateCartItem, removeCartItem }) => {
  const key = cartLineKey(item);
  const isQuote = !isPricedLine(item);
  const hasDiscount = item.discount > 0;
  const unitPrice = hasDiscount
    ? calculateDiscountedPrice(Number(item.price ?? 0), item.discount)
    : Number(item.price ?? 0);
  const computedLineTotal: number =
    typeof item.lineTotal === "number" ? item.lineTotal : unitPrice * Number(item.quantity ?? 0);

  return (
    <CartItemCard>
      <Box>
        <Link to={"/producto/" + item.id}>
          <Typography variant={"subtitle2"} fontWeight={"bold"} color={"text.primary"}>
            {item.name}
          </Typography>
        </Link>
        <AvailabilityChip item={item} />
      </Box>
      <CartItemRow>
        <QuantityControls>
          <QuantityButton>
            <IconButton
              onClick={() => updateCartItem(key, item.quantity - 1)}
              disabled={item.quantity === 1}
              aria-label={CART_TEXTS.decrease}
            >
              <Remove />
            </IconButton>
          </QuantityButton>
          <Box p={1}>
            <Typography>{item.quantity}</Typography>
          </Box>
          <QuantityButton>
            <IconButton
              onClick={() => updateCartItem(key, item.quantity + 1)}
              aria-label={CART_TEXTS.increase}
            >
              <Add />
            </IconButton>
          </QuantityButton>
        </QuantityControls>

        {/* A quote line has no price and no subtotal — printing `$0` twice next to a product the
            shopper does want is the misreading §3.4 exists to prevent. */}
        {isQuote ? (
          <Box px={1} flexGrow={1}>
            <QuoteNote variant="subtitle2">{CART_TEXTS.quoteLine}</QuoteNote>
          </Box>
        ) : (
          <>
            <Box px={1}>
              <Typography fontSize={"small"}>{"Precio"}</Typography>
              <Typography
                variant={"subtitle2"}
                fontWeight={"bold"}
                fontSize={{ xs: "0.7rem", sm: "0.875rem" }}
                color={hasDiscount ? "success.main" : "primary"}
              >
                {formatCOP(unitPrice)}
                {hasDiscount && (
                  <Typography
                    component="span"
                    fontSize="0.65rem"
                    color="error.main"
                    fontWeight="bold"
                    ml={0.5}
                  >
                    -{item.discount}%
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box pr={1}>
              <Typography fontSize={"small"}>{"Subtotal"}</Typography>
              <Typography
                variant={"subtitle2"}
                fontWeight={"bold"}
                fontSize={{ xs: "0.7rem", sm: "0.875rem" }}
                color={"secondary"}
              >
                {formatCOP(Number(computedLineTotal))}
              </Typography>
            </Box>
          </>
        )}
        <DeleteButtonWrapper>
          <IconButton
            onClick={() => removeCartItem(key)}
            color="error"
            aria-label={CART_TEXTS.remove}
          >
            <Delete />
          </IconButton>
        </DeleteButtonWrapper>
      </CartItemRow>
    </CartItemCard>
  );
};

export default CartItemComponent;
